import imageHandler from '@/utils/imageHandler';
import dateHandler from '@/utils/dateHandler';
import watermark from 'watermarkjs';
import localForageService from '@/services/localForage';
import { useStoreManager } from '@/stores/storeManager';
import { TABLE } from '@/constants';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { createReadingIfNotExists } from "./readingManager";

const addNewReadingPicture = (localForageFileId, readingId, creationDate, configuration, longitude = null, latitude = null) => {
  const storeManager = useStoreManager();
  const readingPicture = {
    reading: readingId,
    creation_date: creationDate,
    latitude: latitude,
    longitude: longitude,
    configuration_id: configuration?.id,
    customer_id: configuration?.customer?.id,
    file: localForageFileId
  };
  return storeManager.add('reading', 'picture', readingPicture);
}

export async function addNewPicture(
  readingId,
  file,
  pointOfSaleSectorName,
  pointOfSaleName,
  pictureOptions,
  userId,
  missionPlanningId,
  configId,
  config,
  longitude = null,
  latitude = null
) {
  let result = false;
  try {
    const creationDate = dateHandler.generateNowUnixTimestamp();
    const uuid = `${moment().format("YYYY_MM_DD")}__${uuidv4()}`;
    const resizedFile = await imageHandler.resizeImage(file, pictureOptions.maxWidth);
    const blob = await addWatermark(resizedFile, creationDate, pointOfSaleSectorName, pointOfSaleName, pictureOptions);
    // iOS 18.6.2 (Safari) IndexedDB can fail when storing File objects. Store a Blob instead.
    const blobWithType = blob.type ? blob : blob.slice(0, blob.size, resizedFile.type);
    await localForageService.storeData(TABLE.QUESTION_PICTURES, uuid, blobWithType);

    const readingIdOutput = await createReadingIfNotExists(
      readingId,
      configId,
      missionPlanningId || null,
      userId,
      config.data || null
    );

    result = await addNewReadingPicture(uuid, readingIdOutput, creationDate, config, longitude, latitude)
  } catch (err) {
    console.log(err);
  }

  return result;
}

export async function editExistingPicture(
    readingPicture,
    file,
    pointOfSaleSectorName,
    pointOfSaleName,
    pictureOptions,
    readingPictureId,
    config,
    longitude = null,
    latitude = null
) {
  let result = false;

  try {
    await localForageService.removeItem(TABLE.QUESTION_PICTURES, readingPicture.file);
    readingPicture.creation_date = dateHandler.generateNowUnixTimestamp();
    readingPicture.configuration_id = config?.id
    readingPicture.customer_id = config?.customer?.id
    readingPicture.latitude = latitude;
    readingPicture.longitude = longitude;
    const resizedFile = await imageHandler.resizeImage(file, pictureOptions.maxWidth);
    const blob = await addWatermark(resizedFile, readingPicture.creation_date, pointOfSaleSectorName, pointOfSaleName, pictureOptions);
    // Store as Blob to avoid iOS Safari IndexedDB File storage issues
    const blobWithType = blob.type ? blob : blob.slice(0, blob.size, resizedFile.type);
    await localForageService.storeData(TABLE.QUESTION_PICTURES, readingPicture.file, blobWithType);

    const storeManager = useStoreManager();
    storeManager.update('reading', 'picture', readingPictureId, readingPicture);

    result = true;
  } catch (err) {
    console.log(err);
  }

  return result;
}

export async function removeExistingPicture(readingPicture, readingPictureId) {
  let result = false;

  try {
    const storeManager = useStoreManager();
    await localForageService.removeItem(TABLE.QUESTION_PICTURES, readingPicture.file)
    if (await storeManager.remove('reading', 'picture', readingPictureId)) {
      result = true;
    } else {
      console.error('An error occurred while deleting!');
    }
  } catch (err) {
    console.log(err);
  }

  return result;
}

export async function getImagesFromLocalForge(pictures) {
  let result = {};

  try {
    for (const [id, picture] of Object.entries(pictures)) {
      const img = await localForageService.retrieveData(TABLE.QUESTION_PICTURES, picture.file);
      const blob = img instanceof Blob ? img : new Blob([img]);
      result[picture.file] = blob ? window.URL.createObjectURL(blob) : null;
    }
  } catch (err) {
    console.log(err);
  }
  return result;
}

export async function getImageFromLocalForge(picture) {
  let response = null;
  try {
    response = await localForageService
      .retrieveData(TABLE.QUESTION_PICTURES, picture.file);
  } catch (err) {
    console.log(err);
  }
  return response;
}


async function addWatermark(
  resizedFile,
  creationDate,
  pointOfSaleSectorName,
  pointOfSaleName,
  pictureOptions
) {
  const text =
    window.$moment.unix(creationDate).format('MM/DD/YYYY - HH:mm:ss') +
    ' - ' +
    pointOfSaleName +
    ' - ' +
    pointOfSaleSectorName;

  return watermark([resizedFile]).blob(function (img) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = img.width;
    canvas.height = img.height;

    ctx.drawImage(img, 0, 0);

    const fontFamily = pictureOptions.watermark.font.replace(/^\d+px\s*/, '');

    const baseFontSize = 100;

    ctx.font = `${baseFontSize}px ${fontFamily}`;

    const measuredWidth = ctx.measureText(text).width;

    const fontSize = Math.floor(
      baseFontSize * canvas.width / measuredWidth
    );

    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = pictureOptions.watermark.color;
    ctx.globalAlpha = pictureOptions.watermark.opacity;

    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';

    const paddingRight = 5;

    ctx.fillText(
      text,
      canvas.width - paddingRight,
      canvas.height
    );

    return canvas;
  });
}